import React, { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { submitCalculatorRequest, testSupabaseConnection } from '@/services/supabaseService';
import FooterInfo from './footer/FooterInfo';
import SubmissionForm from './footer/SubmissionForm';

/**
 * Site footer with calculator request form.
 * Supabase connection is tested only on form submission, not on every page load.
 */
const Footer: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (data: { email: string; calculatorDescription: string }) => {
    try {
      setIsSubmitting(true);
      
      // Test connection only when user actually submits
      const connectionTest = await testSupabaseConnection();
      if (!connectionTest.success) {
        toast({
          title: "Connection Error",
          description: "Unable to connect to the database. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      await submitCalculatorRequest(data.email, data.calculatorDescription);
      toast({
        title: "Request Submitted Successfully",
        description: "Thank you for your calculator suggestion! We'll review it soon.",
      });
      
    } catch (error) {
      let errorMessage = "Failed to submit request. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes("permission denied")) {
          errorMessage = "Permission denied for this operation. This might be a database configuration issue.";
        } else if (error.message.includes("network")) {
          errorMessage = "Network error. Please check your internet connection.";
        } else {
          // Log internal details for debugging; show generic message to users
          console.error('Submission error details:', error.message);
          errorMessage = "Unable to submit your request. Please try again later.";
        }
      }
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-muted py-12 mt-20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FooterInfo />
          
          <div>
            <h4 className="font-semibold text-foreground mb-4">Send Feedback / Request a Calculator</h4>
            
            <SubmissionForm 
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              isDisabled={false}
            />
          </div>
        </div>
        
        <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-muted-foreground">© {new Date().getFullYear()} GetCalculator. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
